import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import { Loader } from "../../Common/Loader";
import { X, Loader as LoaderIcon } from "lucide-react";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";
import DeleteModal from "Common/DeleteModal";
import moment from "moment";
import Drawer from "Common/Components/Drawer";
import { addProgram, updateProgram } from "helpers/backend_helper";
import { useAuth } from "hooks/useStore";
import { useProgramStore } from "store/useProgramStore";

type Props = { onHide: () => void; show: boolean, data: any; onSave: (result: any) => void };
export const AddProgram: React.FC<Props> = ({ onHide, show, data, onSave }) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [programData, setProgramData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { user } = useAuth();
  const { fetchPrograms } = useProgramStore();
  
  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const uiHelper = new Common();

  useEffect(() => {
    if (data?.programId) {
      setIsEdit(true);
      loadProgram();
    } else {
      setIsEdit(false);
      setProgramData({});
      validation.resetForm();
    }
  }, [data]);

  const loadProgram = async () => {
    setIsLoading(true);
    try {
      setProgramData(data);

      // Set form values
      validation.setValues({
        programId: data.programId,
        clientId: data.clientId || 1,
        programName: data.programName,
        description: data.description || '',
        startDate: data.startDate ? moment(data.startDate).format("YYYY-MM-DD") : "",
        endDate: data.endDate ? moment(data.endDate).format("YYYY-MM-DD") : ""
      });
    } catch (error) {
      console.error("Error loading program:", error);
      uiHelper.showErrorMessage("Error loading program data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawer = () => { onHide(); }

  // validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      programId: (data && data.programId) || 0,
      clientId: (data && data.clientId) || 1,
      programName: (data && data.programName) || "",
      description: (data && data.description) || "",
      startDate: (data && data.startDate) ? moment(data.startDate).format("YYYY-MM-DD") : "",
      endDate: (data && data.endDate) ? moment(data.endDate).format("YYYY-MM-DD") : ""
    },
    validationSchema: Yup.object({
      programName: Yup.string().required("Please enter program name"),
      description: Yup.string(),
      startDate: Yup.string().required("Please select start date"),
      endDate: Yup.string().required("Please select end date")
        .test('is-after-start', 'End date must be after start date', function(value) {
          const { startDate } = this.parent;
          if (!startDate || !value) return true;
          return moment(value).isAfter(moment(startDate));
        })
    }),
    onSubmit: (values) => {
      handleSave(values);
    }
  });

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      const formData = {
        ...values,
        createdBy: user?.userId ?? 1,
        updatedBy: user?.userId ?? 1
      };

      let response;
      if (isEdit && values.programId) {
        // Update existing program
        response = await updateProgram(formData);
      } else {
        // Create new program
        response = await addProgram(formData);
      }
      
      uiHelper.showSuccessMessage(
        `Program has been successfully ${isEdit ? 'updated' : 'saved'}.`
      );

      // Refresh the program store
      try {
        await fetchPrograms();
        console.log('Program store refreshed successfully');
      } catch (error) {
        console.error('Error refreshing program store:', error);
        // Don't block the success flow if store refresh fails
      }

      onSave(response.data);
      //onHide();
    } catch (error) {
      console.error("Error saving program:", error);
      uiHelper.showErrorMessage("Error saving program data");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <React.Fragment>
      <Drawer show={show} onHide={handleDrawer} id="programSidePenal" drawer-end="true" className="fixed inset-y-0 flex flex-col  
      transition-transform duration-600 ease-in-out transform bg-white shadow 
     dark:bg-zink-600 ltr:right-0 rtl:left-0  w-5/6 flex  z-drawer">

        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="grow">
            <h5 className="mb-0 text-16">{isEdit ? "Edit" : "Add"} Program</h5>
          </div>
          <div className="shrink-0">
            <div className="flex items-center justify-end gap-[200px]">
              <div className="flex items-center gap-2 group">

              </div>
              <Drawer.Header data-drawer-close="programSidePenal" className="transition-all duration-150 ease-linear text-slate-500 hover:text-slate-800">
                <X className="size-4"></X>
              </Drawer.Header>
            </div>
          </div>
        </div>

        <Drawer.Body className="h-full p-0 overflow-y-auto">
          <div className="flex h-full">
            {/* Form Section - 100% width */}
            <div className="w-full h-full overflow-y-auto">
              <div className="h-full overflow-y-auto product-list">
                <div className="row">
                  <div className="col-xl-12">
                    <div className="card">
                      <div className="card-body">
                        {isLoading ? (
                          <Loader />
                        ) : (
                          <form
                            id="programForm"
                            className="needs-validation"
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            <div className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                
                                {/* LEFT PANEL - Program Information */}
                                <div className="space-y-6">
                                  
                                  {/* Program Details Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        🎯 Program Information
                                      </h6>
                                    </div>
                                    
                                    <div className="space-y-4">
                                      <div>
                                        <label className="form-label" htmlFor="programName">
                                          Program Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="programName"
                                          name="programName"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.programName && validation.errors.programName ? "is-invalid" : ""}`}
                                          placeholder="Enter program name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.programName}
                                        />
                                        {validation.touched.programName && validation.errors.programName ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.programName?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="description">
                                          Description
                                        </label>
                                        <textarea
                                          id="description"
                                          name="description"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter program description (optional)"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.description}
                                          rows={4}
                                        />
                                        {validation.touched.description && validation.errors.description ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.description?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* RIGHT PANEL - Schedule Information */}
                                <div className="space-y-6">
                                  
                                  {/* Schedule Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        📅 Schedule Information
                                      </h6>
                                    </div>
                                    
                                    <div className="space-y-4">
                                      <div>
                                        <label className="form-label" htmlFor="startDate">
                                          Start Date <span className="text-danger">*</span>
                                        </label>
                                        <Flatpickr
                                          id="startDate"
                                          name="startDate"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.startDate && validation.errors.startDate ? "is-invalid" : ""}`}
                                          placeholder="Select start date"
                                          options={{
                                            dateFormat: "Y-m-d",
                                            defaultDate: validation.values.startDate || undefined
                                          }}
                                          onChange={(date: any) => {
                                            validation.setFieldValue(
                                              "startDate",
                                              date.length > 0 ? moment(date[0]).format("YYYY-MM-DD") : ""
                                            );
                                          }}
                                          value={validation.values.startDate || undefined}
                                        />
                                        {validation.touched.startDate && validation.errors.startDate ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.startDate?.toString()}
                                          </div>
                                        ) : null}
                                      </div>

                                      <div>
                                        <label className="form-label" htmlFor="endDate">
                                          End Date <span className="text-danger">*</span>
                                        </label>
                                        <Flatpickr
                                          id="endDate"
                                          name="endDate"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.endDate && validation.errors.endDate ? "is-invalid" : ""}`}
                                          placeholder="Select end date"
                                          options={{
                                            dateFormat: "Y-m-d",
                                            minDate: validation.values.startDate || "today",
                                            defaultDate: validation.values.endDate || undefined
                                          }}
                                          onChange={(date: any) => {
                                            validation.setFieldValue(
                                              "endDate",
                                              date.length > 0 ? moment(date[0]).format("YYYY-MM-DD") : ""
                                            );
                                          }}
                                          value={validation.values.endDate || undefined}
                                        />
                                        {validation.touched.endDate && validation.errors.endDate ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.endDate?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </form>
                        )}
                        <ToastContainer closeButton={false} limit={1} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer.Body>
        
        <Drawer.Footer className="flex items-center justify-end p-4 border-t border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-600">
          <div className="flex gap-2">
            <button
              type="button"
              className="text-slate-500 btn bg-white border-slate-200 hover:text-slate-600 hover:bg-slate-100 hover:border-slate-200 focus:text-slate-600 focus:bg-slate-100 focus:border-slate-200 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-100 active:border-slate-200 active:ring active:ring-slate-100 dark:bg-zink-700 dark:hover:bg-zink-600 dark:border-zink-500 dark:text-zink-200"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="programForm"
              className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <LoaderIcon className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                  Saving...
                </>
              ) : "Save"}
            </button>
          </div>
        </Drawer.Footer>
      </Drawer>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={() => {}}
        buttonCaption="Yes, Delete It!"
      />
    </React.Fragment>
  );
}

export default AddProgram;